import { useCallback, useEffect, useMemo } from "react";
import { useQueryParams } from "~/hook/useQueryParams";
import { ActionType, DEFAULT_TRADE_TOKEN } from "~/pages/constants";
import { useAppDispatch, useAppSelector } from "~/store";
import {
    updatePairedToken,
    updatePairedTokensCacheForChainId,
    updateTradeActionType,
    updateTradeActionTypeCacheForKey,
    updateTradeToken,
} from "~/store/trade";
import { TradeToken, PairedToken, TokenBase } from "~/types";
import { useSearchParams } from "react-router";
import localStorage from "~/tools/localStorage";

const useTradeTokenInfo = () => {
    const dispatch = useAppDispatch();
    const [searchParams, setSearchParams] = useSearchParams();

    const params = useQueryParams<{
        chain_id?: string;
        contract?: string;
        order_id?: string;
        action_type?: ActionType;
    }>();

    const {
        tradeToken: storedTradeToken,
        pairedToken: storedPairedToken,
        tradeActionType: storedActionType,
        pairedTokensCache,
        tradeTokenActionTypeCache,
    } = useAppSelector((state) => state.trade);

    /**
     *  TradeToken 取值逻辑：
     *
     * +-----------+-----------+-----------+
     * |           |   有缓存  |   无缓存  |
     * +-----------+-----------+-----------+
     * | 有URL参数 | URL参数值 | URL参数值 |
     * +-----------+-----------+-----------+
     * | 无URL参数 |   缓存值  |   默认值  |
     * +-----------+-----------+-----------+
     *
     */
    const tradeToken = useMemo(() => {
        if (
            (params.chain_id && params.chain_id.length > 0) ||
            (params.contract && params.contract.length > 0)
        ) {
            return null;
        }

        if (storedTradeToken?.token_base?.chain_id) return storedTradeToken;

        const derivedPartialTokenBase: Partial<TokenBase> = {
            chain_id: Number(DEFAULT_TRADE_TOKEN.chain_id),
            contract: DEFAULT_TRADE_TOKEN.contract,
        };

        return { token_base: derivedPartialTokenBase } as TradeToken;
    }, [storedTradeToken, params.chain_id, params.contract]);

    /**
     *  PairedToken 取值逻辑：
     *
     * +-----------+-----------+-----------+
     * |           |   有缓存  |   无缓存  |
     * +-----------+-----------+-----------+
     * | 有URL参数 |   缓存值  |    空值   |
     * +-----------+-----------+-----------+
     * | 无URL参数 |   缓存值  |    空值   |
     * +-----------+-----------+-----------+
     *
     * 取到缓存值之后做一次数据检查，不匹配则使用空值
     */
    const pairedToken = useMemo(() => {
        const tradeTokenChainId = String(tradeToken?.token_base?.chain_id);
        const tradeTokenContract = String(tradeToken?.token_base?.contract);

        // 同链不同币
        if (
            tradeToken?.token_base?.chain_id &&
            String(storedPairedToken?.token_base?.chain_id) ===
                tradeTokenChainId &&
            String(storedPairedToken?.token_base?.contract) !==
                tradeTokenContract
        ) {
            return storedPairedToken;
        }

        const cachedPairedToken = (pairedTokensCache ?? {})[tradeTokenChainId];
        // 同链不同币
        if (
            cachedPairedToken &&
            String(cachedPairedToken?.token_base?.chain_id) ===
                tradeTokenChainId &&
            String(cachedPairedToken?.token_base?.contract) !==
                tradeTokenContract
        ) {
            return cachedPairedToken;
        }

        return null;
    }, [
        tradeToken?.token_base?.chain_id,
        tradeToken?.token_base?.contract,
        storedPairedToken,
        pairedTokensCache,
    ]);

    /**
     *  ActionType 取值逻辑：
     *
     * +-----------+-----------+-----------+
     * |           |   有缓存  |   无缓存  |
     * +-----------+-----------+-----------+
     * | 有URL参数 | URL参数值 | URL参数值 |
     * +-----------+-----------+-----------+
     * | 无URL参数 |   缓存值  |   默认值  |
     * +-----------+-----------+-----------+
     *
     * 取到缓存值之后做一次数据检查，不匹配则使用默认值
     */
    const tradeActiveType = useMemo(() => {
        if (storedActionType) return storedActionType;

        const chain_id = tradeToken?.token_base?.chain_id;
        const contract = tradeToken?.token_base?.contract;
        const cache_key = `${chain_id}:${contract}`;

        return (tradeTokenActionTypeCache ?? {})[cache_key] ?? ActionType.Buy;
    }, [
        params.action_type,
        tradeToken?.token_base?.chain_id,
        tradeToken?.token_base?.contract?.toLowerCase(),
        tradeTokenActionTypeCache,
    ]);

    /**
     * 使用在无需自动计算 PairedToken 和 ActionType 的场景，如订单列表的 Buy Now / Sell Now
     */
    const setTradeTokenPure = useCallback((token: TradeToken) => {
        dispatch(updateTradeToken(token));
        localStorage.set("__uxuy_dex_cached_trade_token__", token);
    }, []);

    /**
     * 设置 TradeToken 的同时，会自动修改对应的 PairedToken 和 ActionType
     */
    const setTradeToken = useCallback(
        (token: TradeToken) => {
            setTradeTokenPure(token);

            const chain_id = String(token?.token_base?.chain_id);
            const contract = token?.token_base?.contract;
            const cache_key = `${chain_id}:${contract}`;
            const newActionType =
                (tradeTokenActionTypeCache ?? {})[cache_key] ?? ActionType.Buy;
            dispatch(updateTradeActionType(newActionType));

            if (
                storedPairedToken &&
                String(storedPairedToken?.token_base?.chain_id) === chain_id
            ) {
                if (
                    String(storedPairedToken?.token_base?.contract) === contract
                ) {
                    dispatch(updatePairedToken(null));
                    dispatch(
                        updatePairedTokensCacheForChainId({
                            chain_id,
                            token: null,
                        })
                    );
                    return;
                } else {
                    dispatch(updatePairedToken(storedPairedToken));
                    dispatch(
                        updatePairedTokensCacheForChainId({
                            chain_id,
                            token: storedPairedToken,
                        })
                    );
                    return;
                }
            }

            const cachedPairedToken = (pairedTokensCache ?? {})[chain_id];
            if (
                cachedPairedToken &&
                String(cachedPairedToken?.token_base?.chain_id) === chain_id
            ) {
                if (
                    String(cachedPairedToken?.token_base?.contract) === contract
                ) {
                    dispatch(updatePairedToken(null));
                    return;
                } else {
                    dispatch(updatePairedToken(cachedPairedToken));
                    return;
                }
            }

            dispatch(updatePairedToken(null));
        },
        [storedPairedToken, pairedTokensCache, tradeTokenActionTypeCache]
    );

    const setPairedToken = useCallback(
        (tradeToken: TradeToken, pairedToken: PairedToken) => {
            const chain_id = String(tradeToken?.token_base?.chain_id);
            const token_chain_id = String(pairedToken?.token_base?.chain_id);
            if (token_chain_id.length > 0 && chain_id !== token_chain_id) {
                // NOTE：极端场景下出现，Maybe 过时的请求结果中 set
                console.trace("UNMATCH");
                return;
            }
            dispatch(updatePairedToken(pairedToken));
            dispatch(
                updatePairedTokensCacheForChainId({
                    chain_id,
                    token: pairedToken,
                })
            );
        },
        []
    );

    const setTradeActiveType = useCallback(
        (tradeToken: TradeToken, type: ActionType) => {
            const chain_id = tradeToken?.token_base?.chain_id;
            const contract = tradeToken?.token_base?.contract;
            const cache_key = `${chain_id}:${contract}`;
            dispatch(updateTradeActionType(type));
            dispatch(
                updateTradeActionTypeCacheForKey({ key: cache_key, type })
            );
        },
        []
    );

    useEffect(() => {
        // 同步 URL 参数状态变化, 到 Store 中
        if (
            (params.chain_id && params.chain_id.length > 0) ||
            (params.contract && params.contract.length > 0)
        ) {
            if (
                !Object.is(
                    Number(params.chain_id),
                    Number(tradeToken?.token_base?.chain_id)
                ) ||
                !Object.is(params.contract, tradeToken?.token_base?.contract)
            ) {
                const derivedPartialTokenBase: Partial<TokenBase> = {
                    chain_id: Number(params.chain_id),
                    contract: params.contract,
                };

                const derivedTradeToken = {
                    token_base: derivedPartialTokenBase,
                } as TradeToken;
                setTradeToken(derivedTradeToken);

                if (params.action_type && params.action_type.length > 0) {
                    const newType =
                        params.action_type === ActionType.Sell.toString()
                            ? ActionType.Sell
                            : params.action_type === ActionType.Buy.toString()
                              ? ActionType.Buy
                              : null;

                    if (newType && newType !== tradeActiveType) {
                        setTradeActiveType(derivedTradeToken, newType);
                    }
                }
            }
            searchParams.delete("chain_id");
            searchParams.delete("contract");
            searchParams.delete("action_type");
            setSearchParams(searchParams);
        }
    }, [
        params.chain_id,
        params.contract?.toLowerCase(),
        params.action_type,
        tradeActiveType,
        tradeToken?.token_base?.chain_id,
        tradeToken?.token_base?.contract.toLowerCase(),
    ]);

    return {
        tradeActiveType,
        setTradeActiveType,
        tradeToken,
        setTradeToken,
        setTradeTokenPure,
        pairedToken,
        setPairedToken,
    };
};

export default useTradeTokenInfo;
